import React, { useState, useEffect } from 'react';
import { getGeneratedImage } from '../../services/storage';
import { downloadImage } from '../../utils/imageUtils';

const ImageViewerScreen = ({ imageId }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        const data = await getGeneratedImage(imageId);
        if (data) {
          setImageData(data);
        } else {
          setError('Image not found or has expired.');
        }
      } catch (err) {
        console.error('Error loading image:', err);
        setError('Failed to load image. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [imageId]);

  const handleDownload = () => {
    if (imageData?.imageData) {
      downloadImage(imageData.imageData, `forensic-memory-${imageId}.jpg`);
    }
  };

  const handleShare = async () => {
    if (!navigator.share || !imageData?.imageData) {
      handleDownload();
      return;
    }
    try {
      const blob = await fetch(imageData.imageData).then((r) => r.blob());
      const file = new File([blob], 'forensic-memory.jpg', { type: 'image/jpeg' });
      await navigator.share({
        title: 'Dubai Police Forensic Memory',
        text: 'View my forensic case file.',
        files: [file]
      });
    } catch (err) {
      console.error('Share failed:', err);
      handleDownload();
    }
  };

  if (loading) {
    return (
      <div className="h-screen relative overflow-hidden crime-scene-gradient text-forensic-text flex items-center justify-center">
        <p className="text-xl uppercase tracking-[0.3em]">Loading case file...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen relative overflow-hidden crime-scene-gradient text-forensic-text flex flex-col items-center justify-center text-center px-6 gap-4">
        <h1 className="text-3xl font-bold uppercase">Unavailable</h1>
        <p className="text-forensic-text-light max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden crime-scene-gradient text-forensic-text px-4 py-10">
      <div className="absolute inset-0 forensic-grid-overlay opacity-10 pointer-events-none" />
      <div className="absolute inset-0 forensic-fingerprint-pattern opacity-10 pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto space-y-6">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-forensic-sage">Dubai Police Forensics</p>
          <h1 className="text-3xl font-bold uppercase text-forensic-text">Your Case File</h1>
          <p className="text-forensic-text-light mt-2">Tap and hold to save, or use the download button below.</p>
        </header>

        <div className="relative rounded-lg border border-forensic-sage/30 overflow-hidden bg-forensic-overlay-light">
          <img
            src={imageData.imageData}
            alt="AI generated memory"
            className="w-full h-full object-contain"
          />
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleDownload}
            className="w-full px-8 py-4 rounded bg-forensic-green text-forensic-white text-sm font-bold uppercase tracking-[0.4em] hover:scale-105 active:scale-95 transition"
          >
            Download Evidence
          </button>
          {navigator.share && (
            <button
              onClick={handleShare}
              className="w-full px-8 py-4 rounded border border-forensic-sage/30 text-forensic-text text-sm font-bold uppercase tracking-[0.4em] hover:bg-forensic-sage/10 transition"
            >
              Share
            </button>
          )}
        </div>

        <p className="text-center text-sm text-forensic-text-light">
          Images expire after 24 hours for visitor privacy.
        </p>
      </div>
    </div>
  );
};

export default ImageViewerScreen;
